import React, { useCallback, useRef, useState } from 'react';
import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';
import { Delete } from '../../../../../ui/Icons';
import { isErrorInfo } from '../../../../../shared/interfaces/error-info.interface';
import {
  ImportResultModel,
  ImportRowResultModel
} from '../../../../../shared/models/landlord.model';
import {
  applyImport,
  downloadImportResult,
  downloadTemplate,
  validateImport
} from '../../../../data.service';

export interface ImportBankDetailsProps {
  config: {
    showModal: boolean;
  };
  eventHandlers: {
    closeHandler: () => void;
    importCompletedHandler: () => void;
  };
}

enum ImportStep {
  SELECT = 'select',
  PREVIEW = 'preview',
  RESULT = 'result'
}

const STEPS: { key: ImportStep; label: string }[] = [
  { key: ImportStep.SELECT, label: 'Upload file' },
  { key: ImportStep.PREVIEW, label: 'Review changes' },
  { key: ImportStep.RESULT, label: 'Done' }
];

const OUTCOME_LABEL: { [outcome: string]: string } = {
  created: 'Created',
  updated: 'Updated',
  unchanged: 'Skipped - no change',
  failed: 'Failed'
};

const OUTCOME_DOT: { [outcome: string]: string } = {
  created: 'bg-primary-600',
  updated: 'bg-primary-300',
  unchanged: 'bg-gray-300',
  failed: 'bg-scarlet'
};

const OUTCOME_TEXT: { [outcome: string]: string } = {
  created: 'text-primary-700',
  updated: 'text-primary-600',
  unchanged: 'text-gray-500',
  failed: 'text-scarlet'
};

const SUMMARY_STATS: {
  key: 'created' | 'updated' | 'unchanged' | 'failed';
  label: string;
  tone: string;
}[] = [
  { key: 'created', label: 'Created', tone: 'text-primary-700' },
  { key: 'updated', label: 'Updated', tone: 'text-primary-600' },
  { key: 'unchanged', label: 'Unchanged', tone: 'text-gray-500' },
  { key: 'failed', label: 'Failed', tone: 'text-scarlet' }
];

function describeChanges(row: ImportRowResultModel): string {
  if (row.errors && row.errors.length > 0) {
    return row.errors.map((error) => error.message).join(', ');
  }
  if (!row.changes || row.changes.length === 0) {
    return '-';
  }
  return row.changes
    .map((change) => `${change.field}: ${change.oldValue || 'empty'} → ${change.newValue}`)
    .join(', ');
}

export const ImportBankDetails: React.FC<ImportBankDetailsProps> = ({ config, eventHandlers }) => {
  const [step, setStep] = useState<ImportStep>(ImportStep.SELECT);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportResultModel | null>(null);
  const [result, setResult] = useState<ImportResultModel | null>(null);
  const [isBusy, setIsBusy] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const active = step === ImportStep.RESULT ? result : preview;
  const activeStepIndex = STEPS.findIndex((s) => s.key === step);

  const handleTemplate = useCallback(() => {
    setIsBusy(true);
    setMessage('');
    downloadTemplate().then((response) => {
      if (isErrorInfo(response)) {
        setMessage('The template could not be downloaded');
      }
      setIsBusy(false);
    });
  }, []);

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files && event.target.files[0];
    setFile(selected || null);
    setPreview(null);
    setMessage('');
  }, []);

  const handleFileRemove = useCallback(() => {
    setFile(null);
    setPreview(null);
    setMessage('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleValidate = useCallback(() => {
    if (!file) {
      return;
    }
    setIsBusy(true);
    setMessage('');
    validateImport(file).then((response) => {
      if (isErrorInfo(response)) {
        setMessage(response.errorMessage || 'The file could not be validated');
      } else {
        setPreview(response);
        setStep(ImportStep.PREVIEW);
      }
      setIsBusy(false);
    });
  }, [file]);

  const handleApply = useCallback(() => {
    if (!file) {
      return;
    }
    setIsBusy(true);
    setMessage('');
    applyImport(file).then((response) => {
      if (isErrorInfo(response)) {
        setMessage(response.errorMessage || 'The import could not be applied');
      } else {
        setResult(response);
        setStep(ImportStep.RESULT);
        eventHandlers.importCompletedHandler();
      }
      setIsBusy(false);
    });
  }, [file, eventHandlers]);

  const handleResultDownload = useCallback(
    (format: 'xlsx' | 'csv') => {
      if (!result) {
        return;
      }
      downloadImportResult(result.rows, format).then((response) => {
        if (isErrorInfo(response)) {
          setMessage('The result could not be downloaded');
        }
      });
    },
    [result]
  );

  const applyDisabled =
    isBusy || !preview || preview.summary.created + preview.summary.updated === 0;

  return (
    <Modal
      config={{
        showModal: config.showModal,
        fullScreen: false,
        maxWidth: step === ImportStep.SELECT ? 'sm' : 'lg',
        showClose: true,
        autoHeight: true
      }}
      eventHandlers={{ closeModalHandler: eventHandlers.closeHandler }}
    >
      <div className="w-full max-w-5xl p-6">
        <h2 className="mb-5 text-2xl font-semibold text-gray-800">Import bank details</h2>

        <div className="mb-6 flex items-center">
          {STEPS.map((item, index) => {
            const isComplete = index < activeStepIndex;
            const isActive = index === activeStepIndex;
            return (
              <React.Fragment key={item.key}>
                <div className="flex items-center gap-2">
                  <div
                    className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${
                      isComplete
                        ? 'bg-primary-500 text-white'
                        : isActive
                        ? 'border-2 border-primary-500 text-primary-700'
                        : 'border border-gray-200 text-gray-500'
                    }`}
                  >
                    {isComplete ? '✓' : index + 1}
                  </div>
                  <span
                    className={`text-base ${
                      isActive ? 'font-medium text-gray-800' : 'text-gray-500'
                    }`}
                  >
                    {item.label}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div
                    className={`mx-3 h-px flex-1 ${isComplete ? 'bg-primary-500' : 'bg-gray-200'}`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {message && (
          <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-base text-scarlet">
            {message}
          </div>
        )}

        {step === ImportStep.SELECT && (
          <div className="flex flex-col gap-5">
            <div className="flex flex-wrap items-center gap-3">
              <Button
                type={ButtonType.default}
                size={ButtonSize.medium}
                disabled={isBusy}
                onClick={handleTemplate}
              >
                Download template
              </Button>
              <span className="text-base text-gray-500">
                Fill the template, then upload it below. Empty cells keep the stored value.
              </span>
            </div>

            <div>
              <label
                htmlFor="bank-import-file"
                className="mb-1 block text-base font-medium text-gray-700"
              >
                File
              </label>
              <div className="flex flex-wrap items-center gap-3">
                <label
                  htmlFor="bank-import-file"
                  className="cursor-pointer rounded-lg border border-gray-300 px-4 py-2 text-base font-medium text-gray-700 hover:border-primary-500 hover:text-primary-700 focus-within:outline-none focus-within:ring-2 focus-within:ring-primary-300"
                >
                  Choose file
                </label>
                <input
                  id="bank-import-file"
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.csv"
                  onChange={handleFileChange}
                  className="sr-only"
                />
                {file ? (
                  <span className="inline-flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-1.5 text-base text-gray-700">
                    {file.name}
                    <button
                      type="button"
                      className="rounded text-gray-500 hover:text-scarlet focus:outline-none focus:ring-2 focus:ring-primary-300"
                      aria-label="Remove selected file"
                      onClick={handleFileRemove}
                    >
                      <Delete size="14px" />
                    </button>
                  </span>
                ) : (
                  <span className="text-base text-gray-500">No file chosen</span>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button
                type={ButtonType.transparent}
                size={ButtonSize.medium}
                onClick={eventHandlers.closeHandler}
              >
                Cancel
              </Button>
              <Button
                type={ButtonType.primary}
                size={ButtonSize.medium}
                disabled={isBusy || !file}
                onClick={handleValidate}
              >
                {isBusy ? 'Checking...' : 'Preview changes'}
              </Button>
            </div>
          </div>
        )}

        {step !== ImportStep.SELECT && active && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap gap-3">
              {SUMMARY_STATS.map((stat) => (
                <div
                  key={stat.key}
                  className="min-w-[7rem] flex-1 rounded-xl border border-gray-200 px-4 py-3"
                >
                  <div className={`text-3xl font-semibold ${stat.tone}`}>
                    {active.summary[stat.key]}
                  </div>
                  <div className="text-sm uppercase tracking-wide text-gray-500">{stat.label}</div>
                </div>
              ))}
            </div>

            {step === ImportStep.PREVIEW && (
              <div className="text-sm text-gray-500">Nothing has been saved yet</div>
            )}

            <div className="max-h-96 overflow-auto rounded-xl border border-gray-200">
              <table className="min-w-full text-base">
                <thead className="sticky top-0 bg-gray-50 text-left text-gray-600">
                  <tr>
                    <th className="px-3 py-2 font-medium">Row</th>
                    <th className="px-3 py-2 font-medium">Landlord</th>
                    <th className="px-3 py-2 font-medium">Outcome</th>
                    <th className="px-3 py-2 font-medium">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {active.rows.map((row) => (
                    <tr
                      key={`${row.rowNumber}-${row.landlordId}`}
                      className="border-t border-gray-100"
                    >
                      <td className="px-3 py-2 text-gray-500">{row.rowNumber}</td>
                      <td className="px-3 py-2 text-gray-800">
                        <div>{row.landlordName || '-'}</div>
                        <div className="text-sm text-gray-500">{row.landlordId}</div>
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex items-center gap-1.5 font-medium ${
                            OUTCOME_TEXT[row.outcome] || 'text-gray-700'
                          }`}
                        >
                          <span
                            className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                              OUTCOME_DOT[row.outcome] || 'bg-gray-300'
                            }`}
                          />
                          {OUTCOME_LABEL[row.outcome] || row.outcome}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-600">{describeChanges(row)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {step === ImportStep.PREVIEW && (
              <div className="flex justify-end gap-3">
                <Button
                  type={ButtonType.transparent}
                  size={ButtonSize.medium}
                  onClick={() => {
                    setStep(ImportStep.SELECT);
                    setPreview(null);
                  }}
                >
                  Back
                </Button>
                <Button
                  type={ButtonType.primary}
                  size={ButtonSize.medium}
                  disabled={applyDisabled}
                  onClick={handleApply}
                >
                  {isBusy ? 'Applying...' : 'Confirm and apply'}
                </Button>
              </div>
            )}

            {step === ImportStep.RESULT && (
              <div className="flex flex-wrap justify-end gap-3">
                <Button
                  type={ButtonType.default}
                  size={ButtonSize.medium}
                  onClick={() => handleResultDownload('xlsx')}
                >
                  Download XLSX
                </Button>
                <Button
                  type={ButtonType.default}
                  size={ButtonSize.medium}
                  onClick={() => handleResultDownload('csv')}
                >
                  Download CSV
                </Button>
                <Button
                  type={ButtonType.primary}
                  size={ButtonSize.medium}
                  onClick={eventHandlers.closeHandler}
                >
                  Done
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ImportBankDetails;
